import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { Pagination } from "@/components/ui/pagination";
import { StatusBadge } from "@/components/ui/status-badge";
import { EmailLogActions } from "@/components/admin/email-logs/EmailLogActions";
import { formatDateTimeWithOptionsBs } from "@/lib/format";

import type { EmailLog, EmailLogsResponse } from "@/types";

export const Route = createFileRoute("/dashboard/admin/email-logs")({
  validateSearch: (search: Record<string, unknown>): { status?: string } => ({
    status: typeof search.status === "string" && search.status ? search.status : undefined,
  }),
  component: AdminEmailLogs,
});

function AdminEmailLogs() {
  const [page, setPage] = useState(1);
  const search = Route.useSearch();
  const [statusFilter, setStatusFilter] = useState<string>(search.status ?? "all");
  const [templateFilter, setTemplateFilter] = useState("");
  const pageSize = 20;

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "email-logs", page, statusFilter, templateFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (templateFilter) params.set("templateName", templateFilter);

      const res = await fetch(`/api/email-logs?${params}`);
      if (!res.ok) throw new Error("Failed to fetch email logs");
      return res.json() as Promise<EmailLogsResponse>;
    },
  });

  const logs = data?.logs ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      <h1
        className="text-2xl font-bold text-foreground"
        style={{ fontFamily: "var(--font-heading)" }}
      >
        Email logovi
      </h1>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Svi statusi</SelectItem>
            <SelectItem value="undelivered">Nisu stigli</SelectItem>
            <SelectItem value="pending">Na čekanju</SelectItem>
            <SelectItem value="dead">Nije poslano</SelectItem>
            <SelectItem value="sent">Poslan</SelectItem>
            <SelectItem value="delivered">Dostavljen</SelectItem>
            <SelectItem value="bounced">Odbijen</SelectItem>
            <SelectItem value="failed">Neuspješan</SelectItem>
          </SelectContent>
        </Select>

        <div className="relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Filter po template imenu..."
            value={templateFilter}
            onChange={(e) => { setTemplateFilter(e.target.value); setPage(1); }}
            className="w-[260px] pl-10"
          />
        </div>

        <span className="text-sm text-muted-foreground">
          Ukupno: {total} zapisa
        </span>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Učitavanje...</div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              Nema email logova za prikaz.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-muted-foreground">
                    <th className="px-6 py-3 font-medium">Primatelj</th>
                    <th className="px-6 py-3 font-medium">Predmet</th>
                    <th className="hidden px-6 py-3 font-medium md:table-cell">Template</th>
                    <th className="px-6 py-3 font-medium">Status</th>
                    <th className="px-6 py-3 font-medium">Datum</th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr
                      key={log.id}
                      className="border-b last:border-0 hover:bg-muted/50 transition-colors"
                    >
                      <td className="px-6 py-4 text-sm">{log.to}</td>
                      <td className="max-w-xs truncate px-6 py-4 text-sm">{log.subject}</td>
                      <td className="hidden px-6 py-4 md:table-cell">
                        <span className="rounded bg-muted px-2 py-0.5 text-xs font-mono">
                          {log.templateName}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          title={[
                            log.error,
                            log.attempts ? `Pokušaja: ${log.attempts}` : null,
                          ]
                            .filter(Boolean)
                            .join("\n") || undefined}
                        >
                          <StatusBadge status={log.status} />
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">
                        {formatDateTimeWithOptionsBs(log.createdAt, {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          timeZone: "Europe/Sarajevo",
                        })}
                      </td>
                      <td className="px-6 py-4">
                        <EmailLogActions log={log} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
}
